/**
 * useAnchor — resolve a `data-help-id` to a live, on-screen DOMRect.
 *
 * See docs/INTERACTIVE_HELP_PLAN.md §5.3. Three behaviours matter:
 *
 *  1. **Viewport intersection, not just a non-zero rect.** Drawers in this
 *     codebase stay mounted when closed, parked at `translateX(100%)` with
 *     full-size rects sitting just off-screen (`.rp-drawer`,
 *     residents-page.css:1396-1417; ResidentsPage renders DeviceDrawer and
 *     EasyAddDeviceDrawer unconditionally). A closed drawer's contents must
 *     count as absent, or the guide would spotlight thin air.
 *  2. **Late mounts.** Data loads and drawer animations mean the element often
 *     appears after the step activates: a MutationObserver plus a slow poll
 *     backstop keeps looking, and reports a `waiting` state meanwhile.
 *  3. **Movement.** Scroll, resize and drawer animation all move the target, so
 *     the rect is re-measured on a rAF loop while a step is active.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

/** How long to keep looking before offering the "can't find it" escape hatch. */
export const ANCHOR_GRACE_MS = 10000;

/** Backstop for mutations an observer can miss (e.g. pure style/class flips). */
const POLL_MS = 500;

/** Has the user asked for less motion? Guarded for non-browser/older envs. */
function prefersReducedMotion() {
    try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {
        return false;
    }
}

/**
 * Is this element actually on screen and interactive-looking?
 *
 * Deliberately not `IntersectionObserver`: we need a synchronous answer inside
 * the measure loop, and the rect is being read anyway.
 */
export function isOnScreen(el) {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return false;
    // Any overlap with the viewport counts; fully off-screen (the parked-drawer
    // case) does not.
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (r.right <= 0 || r.bottom <= 0 || r.left >= vw || r.top >= vh) return false;
    // `visibility: hidden` / `display: none` ancestors produce zero rects, but
    // opacity:0 does not — treat a fully transparent target as absent too.
    const cs = window.getComputedStyle(el);
    if (cs.visibility === 'hidden' || cs.display === 'none' || cs.opacity === '0') return false;
    return true;
}

/**
 * Is the element entirely inside the viewport, with a little breathing room?
 *
 * Used to decide whether scrolling is warranted at all — see the note in
 * `measure()` about menus that close on scroll.
 */
export function isFullyVisible(el, margin = 8) {
    if (!el) return false;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return r.top >= margin && r.left >= margin && r.bottom <= vh - margin && r.right <= vw - margin;
}

/** First on-screen match for a help id, or null. */
export function findAnchor(helpId) {
    if (!helpId) return null;
    const nodes = document.querySelectorAll(`[data-help-id="${helpId}"]`);
    for (const el of nodes) {
        if (isOnScreen(el)) return el;
    }
    return null;
}

/** How many elements carry this id at all, on-screen or not (dev audit). */
export function countAnchors(helpId) {
    if (!helpId) return 0;
    return document.querySelectorAll(`[data-help-id="${helpId}"]`).length;
}

function sameRect(a, b) {
    if (!a || !b) return a === b;
    return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

/**
 * @param {string|null} helpId — anchor to track; null disables the hook
 * @param {boolean} active — false tears everything down (no idle observers)
 * @returns {{ rect: DOMRect|null, status: 'idle'|'waiting'|'found'|'missing' }}
 */
export default function useAnchor(helpId, active = true) {
    const [rect, setRect] = useState(null);
    const [status, setStatus] = useState('idle');

    const rectRef = useRef(null);
    const rafRef = useRef(0);
    const scrolledForRef = useRef(null);

    const measure = useCallback(() => {
        const el = findAnchor(helpId);
        if (!el) {
            if (rectRef.current !== null) {
                rectRef.current = null;
                setRect(null);
            }
            return false;
        }
        /* Bring it into view once per anchor, and only when it isn't already
           fully visible.
           Scrolling unnecessarily isn't merely untidy here — several menus in this
           app close on ANY scroll (the residents row menu does:
           `window.addEventListener('scroll', onScroll, true)` in
           ResidentTable.jsx). So a gratuitous scrollIntoView would slam shut the
           very menu the step is pointing at. */
        if (scrolledForRef.current !== helpId) {
            scrolledForRef.current = helpId;
            if (!isFullyVisible(el)) {
                try {
                    el.scrollIntoView({
                        block: 'center',
                        inline: 'nearest',
                        /* Smooth scrolling is motion the user may have opted out
                           of; 'auto' jumps instead. Read per-call rather than
                           cached so a mid-session OS change is honoured. */
                        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
                    });
                } catch {
                    el.scrollIntoView();
                }
            }
        }
        const next = el.getBoundingClientRect();
        if (!sameRect(rectRef.current, next)) {
            rectRef.current = next;
            setRect(next);
        }
        return true;
    }, [helpId]);

    useEffect(() => {
        if (!active || !helpId) {
            setStatus('idle');
            setRect(null);
            rectRef.current = null;
            scrolledForRef.current = null;
            return undefined;
        }

        let cancelled = false;
        let found = false;
        scrolledForRef.current = null;
        setStatus('waiting');

        const graceTimer = setTimeout(() => {
            if (!cancelled && !found) setStatus('missing');
        }, ANCHOR_GRACE_MS);

        const attempt = () => {
            if (cancelled) return;
            const hit = measure();
            if (hit && !found) {
                found = true;
                setStatus('found');
            } else if (!hit && found) {
                // Disappeared after being found (dialog closed) — back to
                // waiting; the engine's context rule decides whether to regress.
                found = false;
                setStatus('waiting');
            }
        };

        attempt();

        // rAF loop keeps the rect glued to the target through scroll, resize and
        // drawer transitions. Only runs while a step is active.
        const loop = () => {
            if (cancelled) return;
            attempt();
            rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);

        const observer = new MutationObserver(() => attempt());
        observer.observe(document.body, { childList: true, subtree: true });
        const poll = setInterval(attempt, POLL_MS);

        return () => {
            cancelled = true;
            clearTimeout(graceTimer);
            clearInterval(poll);
            observer.disconnect();
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = 0;
        };
    }, [helpId, active, measure]);

    return { rect, status };
}
